import { keys, makeAutoObservable, runInAction, autorun } from "mobx";
import {
    adminGetAgents,
    adminGetArtifacts,
    adminGetTags,
    getOrganisations,
    loginForAccessToken,
    logout as _logout,
    readUsersMe,
    getClients,
    adminGetArtifact,
    chatCreateArtifact,
    adminGetTag,
    getEvent,
    adminGetDocument,
} from "@/api/sdk.gen";
import { tag, tag_list_item, tag_revision } from "./typing/tags";
import { artifact_list_item, artifact, artifact_revision } from "./typing/artifacts";
// Aliased: a bare `document` import shadows the global DOM `document`.
import type { document as document_type, document_list_item } from "./typing/documents";
import { sourceParent } from "./workspace/chat/artifactSource";
import { ConversationSelection, deletedKeysFromHistory, latestMessageId, ObjectKey, resolveSelection, selectionFromHistory, withoutKeys } from "./workspace/chat/conversationSelection";
import { calendar_event, calendar_event_list_item, calendar_event_revision } from "./typing/events";
import { organisation_list_item } from "./typing/organisations";
import { chat_list_item, ChatMessage, ConversationBlock } from "./typing/chat";
import { useNavigate } from "react-router-dom";
import { BoldItalicUnderlineTogglesProps } from "@mdxeditor/editor";
import { Request } from "@/components/CRUD";
import { AdminGetArtifactData, AdminGetArtifactsData, AdminGetDocumentData, AdminGetTagData, GetEventData } from "@/api";
import { appState } from "./appState";

export type ChatObjectType = "tag" | "artifact" | "event" | "document";
type ObjectValue = {
    type: ChatObjectType;
    id: number;
    label: string;
    isSelected?: boolean;
    edit?: boolean;
    msgId?: number;
    revision_id?: number;
    created_at: string;
    revision_index?: number;
    parent?: { type: ChatObjectType; id: number };
    child_index?: number;
};

export type ChatArtifact = ObjectValue & {
    type: "artifact";
    artifact: artifact | artifact_list_item;
    unsaved_changes: boolean;
    temp_obj?: {
        variable_name: string;
        content: string;
        tag_ids: number[];
        revision_id: number | null;
        event_id: number | null;
    };
    revisions?: artifact_revision[] | undefined;
};

export type ChatTag = ObjectValue & {
    type: "tag";
    tag: tag | tag_list_item;
    unsaved_changes: boolean;
    temp_obj?: {
        revision_id: number | null;
        name: string;
        description: string;
        scope: "me" | "organisation";
        permission_type: number;
        tpr_organisation_ids: number[];
        attributes: Record<string, any>;
    };
    revisions?: tag_revision[] | undefined;
};

export type ChatEvent = ObjectValue & {
    type: "event";
    event: calendar_event | calendar_event_list_item;
    unsaved_changes: boolean;
    temp_obj?: {
        title: string;
        description: string | null;
        location: string | null;
        start_time: string;
        end_time: string;
        all_day: boolean;
        timezone: string;
        tag_id: number;
        revision_id: number | null;
    };
    revisions?: calendar_event_revision[] | undefined;
};

/**
 * Documents are immutable sources. temp_obj and revisions are declared as
 * permanently undefined rather than omitted: the shared object-editor plumbing
 * reads those fields off the union, and typing them this way keeps documents
 * readable there while making it a type error to ever put a pending edit on one.
 */
export type ChatDocument = ObjectValue & {
    type: "document";
    document: document_type | document_list_item;
    unsaved_changes: false;
    temp_obj?: undefined;
    revisions?: undefined;
};

class SortedMap<T extends ObjectValue> {
    private _map = new Map<string, T>();

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Get a value by key with proper type narrowing based on the type field
     * @param key the key
     */
    get<K extends T["type"]>(key: { type: K; id: number } | null): Extract<T, { type: K }> | undefined {
        if (!(key && key.type && key.id)) return;
        // Need to find the key since objects aren't reference equal
        return this._map.get(key.type + key.id?.toString()) as Extract<T, { type: K }>;
    }

    /**
     * Check if a key exists
     */

    /**
     * Clear all entries
     */
    clear() {
        this._map.clear();
    }

    /**
     * Get all entries
     * if rootKeys is provided, only return trees starting from those keys (inclusive)
     */
    entries(rootKeys?: { type: string; id: number }[]) {
        const childrenMap = new Map<string, T[]>();
        for (const val of this._map.values()) {
            if (val.parent) {
                const parentKey = val.parent.type + val.parent.id?.toString();
                if (!childrenMap.has(parentKey)) {
                    childrenMap.set(parentKey, []);
                }
                childrenMap.get(parentKey)!.push(val);
            }
        }

        const buildTree = (val: T): { key: { type: ChatObjectType; id: number }; value: T; children: any[] } => {
            const children = [];
            const myKey = val.type + val.id?.toString();
            const childObjs = childrenMap.get(myKey);
            if (childObjs) {
                for (const childObj of childObjs) {
                    children.push(buildTree(childObj as T));
                }
            }
            return {
                key: { type: val.type, id: val.id },
                value: val,
                children: children,
            };
        };

        const roots = [];
        if (rootKeys) {
            for (const key of rootKeys) {
                const val = this.get(key as any);
                if (val) roots.push(buildTree(val));
            }
        } else {
            for (const val of this._map.values()) {
                const hasParentInMap = val.parent && this._map.has(val.parent.type + val.parent.id?.toString());
                if (!hasParentInMap) {
                    roots.push(buildTree(val));
                }
            }
        }

        return roots;
    }

    /**
     * Get all keys
     */
    keys() {
        return this._map.keys();
    }

    /**
     * Get all values
     */
    values() {
        return this._map.values();
    }

    /**
     * Get size
     */
    get size() {
        return this._map.size;
    }

    /**
     * Make iterable
     */
    [Symbol.iterator]() {
        return this._map[Symbol.iterator]();
    }

    /**
     * Deep merge utility - recursively merges objects
     */
    private deepMerge<T>(target: T, source: Partial<T>): T {
        const result = { ...target };

        for (const key in source) {
            const sourceValue = source[key];
            const targetValue = target[key];
            // console.log("Deep merging key:", key, "Source value:", sourceValue, "Target value:", targetValue);
            if (sourceValue === undefined) {
                delete result[key];
                continue;
            }

            // If both are plain objects, merge recursively
            if (sourceValue && typeof sourceValue === "object" && !Array.isArray(sourceValue) && targetValue && typeof targetValue === "object" && !Array.isArray(targetValue)) {
                result[key] = this.deepMerge(targetValue, sourceValue as any);
            } else {
                result[key] = sourceValue as any;
            }
        }
        return result;
    }

    /**
     * update function to do a partial update on a given entry
     * @param key the key
     * @param updater (old_val) => ({some_var: "some_value"}), null to delete the entry
     */
    update<K extends T["type"]>(entries: { key: { type: K; id: number }; value: any | null }[]) {
        runInAction(() => {
            entries.map((x) => {
                if (x.key.id === undefined || x.key.type === undefined) return;

                const kstr = x.key.type + x.key.id.toString();
                // console.log("SortedMap update:", x, this._map.get(kstr));
                const existing = this._map.get(kstr);
                this._map.delete(kstr);
                if (x.value === null) {
                    return;
                }

                if (existing && existing.type === x.key.type) {
                    this._map.set(kstr, this.deepMerge(existing, x.value as Extract<T, { type: K }>));
                } else {
                    // Seed type/id from the key: a partial update (e.g. just isSelected) must still yield a usable entry.
                    this._map.set(kstr, this.deepMerge({ type: x.key.type, id: x.key.id } as Extract<T, { type: K }>, x.value as Extract<T, { type: K }>));
                }

                // if (this._map.get(kstr) === undefined) {
                //     this._map.delete(kstr);
                // }
                // console.log("Updated SortedMap entry:", this._map.get(kstr));
            });

            const sorted = [...this._map.entries()].sort((a, b) => {
                const aTime = a[1].created_at ? Date.parse(a[1].created_at) : 0;
                const bTime = b[1].created_at ? Date.parse(b[1].created_at) : 0;
                if (aTime !== bTime) return aTime - bTime;
                const idComp = (a[1].id ?? 0) - (b[1].id ?? 0);
                if (idComp !== 0) return idComp;
                return (a[1].type ?? "").localeCompare(b[1].type ?? "");
            });
            this._map.clear();
            sorted.forEach(([k, v]) => this._map.set(k, v));
        });
        return this;
    }
}

export interface ScrollToObjectKeyDetail {
    key: { type: ChatObjectType; id: number; revision_id?: number };
}

export function dispatchScrollToObjectKeyEvent(key: { type: ChatObjectType; id: number; revision_id?: number }): void {
    const event = new CustomEvent<ScrollToObjectKeyDetail>("scroll-to-object-key", {
        detail: { key },
        bubbles: true,
    });

    document.dispatchEvent(event);
}

export type ObjectEditorTempArtifactValues = {
    type: "artifact";
    variable_name: string;
    content: string;
    tag_ids: number[];
    revision_id: number | null;
    event_id: number | null;
    document_id: number | null;
};

export type ObjectEditorTempTagValues = {
    type: "tag";
    name: string;
    description: string;
    scope: "me" | "organisation";
    permission_type: number;
    tpr_organisation_ids: number[];
    attributes: Record<string, any>;
};

export type ObjectEditorTempEventValues = {
    type: "event";
    title: string;
    description: string | null;
    location: string | null;
    start_time: string;
    end_time: string;
    all_day: boolean;
    timezone: string;
    tag_id: number;
};

export type ObjectEditorTemp = ObjectEditorTempArtifactValues | ObjectEditorTempTagValues | ObjectEditorTempEventValues;

type ChatObject = ChatArtifact | ChatTag | ChatEvent | ChatDocument;

export class ChatState {
    conversation: ConversationBlock[] = [];
    streamingContent: string = "";

    chatObjects = new SortedMap<ChatObject>();
    selectedObjectKey: { type: ChatObjectType; id: number } | null = null;
    delayedSelectedObjectKey: { type: ChatObjectType; id: number } | null = null;
    /** Remembered selection per conversation id; survives `clear()` so switching chats keeps each one's picks. */
    conversationSelections = new Map<number, ConversationSelection>();
    pendingTranscriptionScroll: {
        eventId: number;
        startLine?: number;
        endLine?: number;
        nonce: number;
    } | null = null;
    mounted: boolean = false;
    showDebugView: boolean = false;
    hasChangedCallback: ((new_obj: ObjectEditorTemp) => boolean) | null = null;

    token_usage: number = 0;
    kroner_usage: number = 0;

    state: "idle" | "awaiting_backend" | "awaiting_llm" | "streaming" | "awaiting_event_summarisation" | "awaiting_document_summarisation" | "conversation_locked" = "idle";
    lastExternalUpdate: number = 0;

    conversationId: number | null = null;
    tagIds: number[] = [];

    /** Messages sent while the agent was running; shown below the thread until the run picks them up. */
    queuedSteers: { id: number; text: string }[] = [];
    private nextQueuedSteerId = 1;

    pendingChanges: Set<string> = new Set();

    flushToLocalDB = () => {
        if (this.pendingChanges.size === 0) return;
        this.pendingChanges.forEach((keyStr) => {
            const [type, idStr] = keyStr.split(":");
            const id = parseInt(idStr);
            const key = { type: type as ChatObjectType, id };
            const obj = this.chatObjects.get(key);
            const storageKey = `temp_obj_${type}_${id}`;

            if (obj?.temp_obj) {
                localStorage.setItem(
                    storageKey,
                    JSON.stringify({
                        ...obj.temp_obj,
                        unsaved_changes: obj.unsaved_changes,
                        _created_at: obj.created_at,
                        _label: obj.label,
                    }),
                );
            } else {
                localStorage.removeItem(storageKey);
            }
        });
        this.pendingChanges.clear();
    };

    setKronerUsage = (val: number) => {
        runInAction(() => {
            this.kroner_usage = val;
        });
    };

    setTokenUsage = (val: number) => {
        runInAction(() => {
            this.token_usage = val;
        });
    };

    loadFromDB = () => {
        runInAction(() => {
            const updates: { key: { type: ChatObjectType; id: number }; value: any }[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith("temp_obj_")) {
                    const parts = key.split("_");
                    if (parts.length === 4) {
                        const type = parts[2] as ChatObjectType;
                        const id = parseInt(parts[3]);
                        try {
                            const stored = JSON.parse(localStorage.getItem(key)!);

                            if (stored.unsaved_changes === false) {
                                localStorage.removeItem(key);
                                continue;
                            }

                            const { _created_at, _label, unsaved_changes, ...temp_obj } = stored;

                            updates.push({
                                key: { type, id },
                                value: {
                                    temp_obj,
                                    unsaved_changes: true,
                                    type,
                                    id,
                                    ...(_created_at && { created_at: _created_at }),
                                    ...(_label && { label: _label }),
                                },
                            });
                        } catch (e) {
                            console.error("Failed to parse temp_obj from local storage", e);
                        }
                    }
                }
            }
            if (updates.length > 0) {
                this.chatObjects.update(updates);
            }
        });
    };

    setState = (val: "idle" | "awaiting_backend" | "awaiting_llm" | "streaming" | "awaiting_event_summarisation" | "awaiting_document_summarisation" | "conversation_locked") => {
        runInAction(() => {
            this.state = val;
        });
    };

    // objectEditorTemp: ObjectEditorTemp | null = null;

    setHasChangedCallback = (hasChangedCallback: ((new_obj: ObjectEditorTemp) => boolean) | null) => {
        this.hasChangedCallback = hasChangedCallback;
    };
    setObjectEditorTemp = (updates: Partial<ObjectEditorTemp> | null, overrideKey?: { type: ChatObjectType; id: number }) => {
        runInAction(() => {
            // console.trace("setObjectEditorTemp called with updates:", updates);
            const key = overrideKey ?? this.selectedObjectKey;
            console.log("SETTING TEMP OBJECT", `key: ${key?.id}, chatState.chatObjects.get(key): ${chatState.chatObjects.get(key)?.id}`);

            const obj = this.chatObjects.get(key);
            const revisions =
                obj?.revisions
                    ?.slice()
                    .sort((a, b) => a.id - b.id)
                    .filter((x) => (x ? true : false)) ?? [];
            const isLastRev = key && obj?.revision_index !== undefined && obj?.revision_index + 1 == revisions.length;
            if (!key || (revisions.length > 0 && !isLastRev)) return;
            if (updates === null) {
                this.chatObjects.update([{ key: key, value: { temp_obj: undefined, unsaved_changes: false } }]);
            } else {
                const new_obj = { ...obj?.temp_obj, ...updates, ...{ type: obj?.type } } as ObjectEditorTemp;

                this.chatObjects.update([{ key: key, value: { temp_obj: new_obj, unsaved_changes: this.hasChangedCallback ? this.hasChangedCallback(new_obj) : false } }]);
            }
            this.pendingChanges.add(`${key.type}:${key.id}`);
        });
    };

    setMounted = (val: boolean) => {
        runInAction(() => {
            this.mounted = val;
        });
    };

    setShowDebugView = (val: boolean) => {
        runInAction(() => {
            this.showDebugView = val;
        });
    };

    flushRevisions = async (key: { type: ChatObjectType; id: number }, revision_id?: number) => {
        runInAction(() => {
            console.log("flushed revisions");

            const curr_rev = this.chatObjects.get(key)?.revisions;
            if (curr_rev && curr_rev.length > 0 && revision_id !== undefined) {
                this.chatObjects.update([{ key: key, value: { revisions: curr_rev.map((x) => (x ? (x.id > revision_id ? x : undefined) : undefined)) } }]);
            } else {
                this.chatObjects.update([{ key: key, value: { revisions: [] } }]);
            }
            return true;
        });
    };

    /**
     * Stores a revision delivered by a conversation message. An editor showing the newest
     * revision, the root or nothing yet moves to the new revision; one parked on an older
     * revision stays put, and re-delivered revisions (remounted foldouts) never move it.
     * `clearedTemp` is the temp_obj patch that discards pending edits of that object type.
     */
    applyIncomingRevision = <K extends "artifact" | "tag" | "event">(
        key: { type: K; id: number },
        revision: NonNullable<Extract<ChatObject, { type: K }>["revisions"]>[number],
        msgId: number | undefined,
        clearedTemp: Record<string, unknown>,
    ) => {
        runInAction(() => {
            const obj = this.chatObjects.get(key) as ChatArtifact | ChatTag | ChatEvent | undefined;
            const revs: any[] = obj?.revisions?.slice() ?? [];
            const known = revs.filter((x) => x != undefined).length;
            const isNew = revs[revision.id] == undefined;
            const index = obj?.revision_index;
            const followsLatest = index === undefined || index === -1 || index === known - 1;
            revs[revision.id] = revision;
            const resetTemp = revision.id > (obj?.temp_obj?.revision_id ?? -1) ? { temp_obj: clearedTemp } : {};
            this.chatObjects.update([{
                key, value: {
                    revisions: revs,
                    msgId,
                    ...(isNew && followsLatest ? { revision_index: known } : {}),
                    ...resetTemp,
                } as any,
            }]);
        });
    };

    _makeStaleGuard = () => {
        const generation = this._fetchGeneration;
        return () => generation !== this._fetchGeneration;
    };

    getObjectByKey = async (key: { type: ChatObjectType; id: number }) => {
        const isStale = this._makeStaleGuard();
        switch (key.type) {
            case "artifact":
                console.log("Fetching artifact by key:", key);
                const art_res = await Request<AdminGetArtifactData, any>(
                    adminGetArtifact,
                    () => ({
                        body: {
                            id: key?.id!,
                        },
                    }),
                    () => {},
                    (data: any) => {
                        if (data.artifact)
                            this.chatObjects.update([
                                {
                                    key: key,
                                    value: {
                                        parent: sourceParent(data.artifact),
                                        artifact: data.artifact,
                                        label: data.artifact.variable_name,
                                        type: "artifact",
                                        id: data.artifact.id,
                                        created_at: data.artifact.created_at,
                                    },
                                },
                            ]);
                    },
                    (error) => {},
                    () => key?.id != undefined && key?.id > 0,
                    isStale,
                )();
                return art_res;
            case "tag":
                // Implement tag fetching logic here, similar to artifact
                console.log("Fetching tag by key:", key);
                const tag_res = await Request<AdminGetTagData, any>(
                    adminGetTag,
                    () => ({
                        body: {
                            id: key?.id!,
                        },
                    }),
                    () => {},
                    (data: any) => {
                        if (data.tag)
                            this.chatObjects.update([
                                {
                                    key: key,
                                    value: { tag: data.tag, label: data.tag.name, type: "tag", id: data.tag.id, created_at: data.tag.created_at },
                                },
                            ]);
                    },
                    (error) => {},
                    () => key?.id != undefined && key?.id > 0,
                    isStale,
                )();
                return tag_res;
            case "event":
                console.log("Fetching event by key:", key);
                const event_res = await Request<GetEventData, any>(
                    getEvent,
                    () => ({
                        body: {
                            id: key?.id!,
                        },
                    }),
                    () => {},
                    (data: any) => {
                        if (data.event)
                            this.chatObjects.update([
                                {
                                    key: key,
                                    value: {
                                        event: data.event,
                                        label: data.event.title,
                                        type: "event",
                                        id: data.event.id,
                                        created_at: data.event.created_at,
                                    },
                                },
                            ]);
                    },
                    (error) => {},
                    () => key?.id != undefined && key?.id > 0,
                    isStale,
                )();
                return event_res;
            case "document":
                console.log("Fetching document by key:", key);
                const doc_res = await Request<AdminGetDocumentData, any>(
                    adminGetDocument,
                    () => ({
                        body: {
                            id: key?.id!,
                        },
                    }),
                    () => {},
                    (data: any) => {
                        if (data.document)
                            this.chatObjects.update([
                                {
                                    key: key,
                                    value: {
                                        document: data.document,
                                        label: data.document.variable_name,
                                        type: "document",
                                        id: data.document.id,
                                        created_at: data.document.created_at,
                                        unsaved_changes: false,
                                    },
                                },
                            ]);
                    },
                    (error) => {},
                    () => key?.id != undefined && key?.id > 0,
                    isStale,
                )();
                return doc_res;
        }
        return false;
    };

    setPendingTranscriptionScroll = (payload: ChatState["pendingTranscriptionScroll"]) => {
        runInAction(() => {
            this.pendingTranscriptionScroll = payload;
        });
    };

    setDelayedSelectedObjectKey = (key: { type: ChatObjectType; id: number } | null) => {
        runInAction(() => {
            this.delayedSelectedObjectKey = key;
        });
    };

    commitDelayedSelectedObjectKey = () => {
        runInAction(() => {
            console.log("COMMIT DELAYED SELECTED OBJECT KEY", this.delayedSelectedObjectKey);
            if (this.delayedSelectedObjectKey) {
                this.setSelectedObjectKey(this.delayedSelectedObjectKey);
                this.delayedSelectedObjectKey = null;
            }
        });
    };

    /** Store the live selection as the remembered selection of `conversationId`. */
    rememberCurrentSelection = (conversationId: number | null = this.conversationId) => {
        if (conversationId == null) return;
        this.conversationSelections.set(conversationId, {
            selectedKeys: this.getSelected(),
            openKey: this.selectedObjectKey ? { ...this.selectedObjectKey } : null,
            messageId: latestMessageId(this.conversation),
        });
    };

    forgetConversationSelection = (conversationId: number | null | undefined) => {
        if (conversationId == null) return;
        this.conversationSelections.delete(conversationId);
    };

    clearSelections = () => {
        this.conversationSelections.clear();
    };

    /** Restore what `conversationId` had selected, or what its agent picked since. */
    applyConversationSelection = (conversationId: number, blocks: ConversationBlock[] = this.conversation) => {
        const chosen = withoutKeys(
            resolveSelection(this.conversationSelections.get(conversationId), selectionFromHistory(blocks)),
            deletedKeysFromHistory(blocks),
        );
        if (chosen) this.conversationSelections.set(conversationId, chosen);

        const keys = chosen?.selectedKeys ?? [];
        const stale = [...this.chatObjects].filter(([_, val]) => val.isSelected).map(([_, val]) => ({ type: val.type, id: val.id }) as ObjectKey);
        this.chatObjects.update(stale.map((key) => ({ key, value: { isSelected: false, child_index: undefined } })));
        this.chatObjects.update(keys.map((key) => ({ key, value: { type: key.type, id: key.id, isSelected: true } })));
        this.selectedObjectKey = keys.length ? (chosen?.openKey ?? keys[0]) : null;
        if (this.selectedObjectKey) dispatchScrollToObjectKeyEvent(this.selectedObjectKey);

        // Objects deleted outside this conversation only show up as a failed fetch.
        const isStale = this._makeStaleGuard();
        for (const key of keys) {
            if (this.chatObjects.get(key)?.created_at) continue;
            void this.getObjectByKey(key).then((found) => {
                if (!found && !isStale()) this.forgetDeletedObjects([key]);
            });
        }
    };

    setSelectedObjectKey = (
        key: { type: ChatObjectType; id: number } | null,
        keep_selection: boolean = false,
        range_selection: boolean = false,
        remove_from_selection: boolean = false,
        scrollToKey?: { type: ChatObjectType; id: number },
        markKeyAsSelected: boolean = true,
    ) => {
        runInAction(async () => {
            if (!keep_selection) {
                // Use the raw iterator ([...this.chatObjects]) instead of entries() so that
                // child objects (those with a parent in the map) are also swept. entries()
                // returns root nodes only, which would leave child artifacts with isSelected:true
                // as ghost selections that can never be cleared.
                const keysToRemove = [...this.chatObjects].filter(([_, val]) => val.isSelected).map(([_, val]) => ({ type: val.type, id: val.id }) as { type: ChatObjectType; id: number });
                this.chatObjects.update(keysToRemove.map((key) => ({ key, value: { isSelected: false, child_index: undefined } })));
            }

            if (range_selection) {
                // set all between selected and key to selected
                if (!this.selectedObjectKey || !key) return;
                const entries = [...this.chatObjects.entries()];
                const selectedIndex = entries.findIndex((x) => x.key.type == this.selectedObjectKey?.type && x.key.id == this.selectedObjectKey?.id);
                const keyIndex = entries.findIndex((x) => x.key.type == key?.type && x.key.id == key?.id);
                if (selectedIndex == -1 || keyIndex == -1) return;
                const [start, end] = selectedIndex < keyIndex ? [selectedIndex, keyIndex] : [keyIndex, selectedIndex];
                const toSelect = entries
                    .slice(start, end + 1)
                    .filter((x) => x && x.value && (x.value.msgId ? true : false))
                    .map((x) => x.key);
                this.chatObjects.update(toSelect.map((x) => ({ key: x, value: { isSelected: true } })));
                // Always mark the clicked key itself as selected (e.g. an event target won't pass the artifact filter above)
                if (key) this.chatObjects.update([{ key: key, value: { isSelected: true } }]);
                this.selectedObjectKey = key;
                dispatchScrollToObjectKeyEvent(scrollToKey ?? this.selectedObjectKey);
                return;
            }

            if (markKeyAsSelected && this.chatObjects.get(key)?.isSelected && remove_from_selection) {
                this.chatObjects.update([{ key: key!, value: { isSelected: false } }]);
                if (this.selectedObjectKey && this.selectedObjectKey.type == key?.type && this.selectedObjectKey.id == key?.id) {
                    this.selectedObjectKey = [...this.chatObjects.entries()].filter((x) => x.value.isSelected == true)?.[0]?.key ?? null;
                }
                return;
            }

            this.selectedObjectKey = key;
            if (key) {
                dispatchScrollToObjectKeyEvent(scrollToKey ?? key);
                if (markKeyAsSelected) {
                    this.chatObjects.update([{ key: key, value: { isSelected: true } }]);
                }
            }
            return;
        });
        this.rememberCurrentSelection();
    };

    /**
     * Plain-click "open" used by both the timeline and objects in the chat: replace the selection,
     * and open a child (e.g. an artifact under an event) inside its parent with the child focused.
     * revisionIndex: undefined opens the latest revision; any number (incl. -1) is used as given.
     */
    openObject = async (key: { type: ChatObjectType; id: number }, revisionIndex?: number) => {
        const obj = this.chatObjects.get(key);
        const parent = obj?.parent as { type: ChatObjectType; id: number } | undefined;
        const revisions = obj?.revisions?.filter((r: any) => r != undefined);
        const revision_index = revisionIndex !== undefined ? revisionIndex : revisions?.length ? revisions.length - 1 : undefined;

        if (parent) {
            if (!this.chatObjects.get(parent)) await this.getObjectByKey(parent);
            runInAction(() => {
                const children = this.chatObjects.entries([parent as any])[0]?.children ?? [];
                const childIndex = children.findIndex((c: any) => c.key.type === key.type && c.key.id === key.id);
                this.setSelectedObjectKey(parent, false, false, false, key, false);
                this.chatObjects.update([
                    { key, value: { isSelected: true, revision_index } },
                    { key: parent, value: { child_index: childIndex >= 0 ? childIndex : undefined } },
                ]);
            });
            return;
        }

        runInAction(() => {
            this.setSelectedObjectKey(key, false);
            this.chatObjects.update([{ key, value: { child_index: undefined, revision_index } }]);
        });
    };

    /** Drops deleted objects from the chat: their entries, the live selection and every conversation's remembered one. */
    forgetDeletedObjects = (keys: ObjectKey[]) => {
        if (keys.length === 0) return;
        runInAction(() => {
            this.chatObjects.update(keys.map((key) => ({ key, value: null })));
            const open = this.selectedObjectKey;
            if (open && keys.some((k) => k.type === open.type && k.id === open.id)) {
                this.selectedObjectKey = this.getSelected()[0] ?? null;
            }
            for (const [conversationId, remembered] of this.conversationSelections) {
                this.conversationSelections.set(conversationId, withoutKeys(remembered, keys)!);
            }
        });
        this.rememberCurrentSelection();
    };

    removeSelectedObjectKey = () => {
        runInAction(() => {
            const keysToRemove = [...this.chatObjects].filter(([_, val]) => val.isSelected).map(([_, val]) => ({ type: val.type, id: val.id }) as { type: ChatObjectType; id: number });
            this.chatObjects.update(keysToRemove.map((key) => ({ key, value: { isSelected: false, child_index: undefined } })));
            this.selectedObjectKey = null;
        });
        this.rememberCurrentSelection();
    };

    getSelected = () => {
        return [...this.chatObjects].filter(([key, val]) => val.isSelected).map(([key, val]) => ({ type: val.type, id: val.id }));
    };

    storageListener = (event: StorageEvent) => {
        if (event.key && event.key.startsWith("temp_obj_")) {
            const parts = event.key.split("_");
            if (parts.length === 4) {
                const type = parts[2] as ChatObjectType;
                const id = parseInt(parts[3]);
                const key = { type, id };

                runInAction(() => {
                    this.lastExternalUpdate++;
                    if (event.newValue === null) {
                        // Item removed
                        this.chatObjects.update([{ key: key, value: { temp_obj: undefined, unsaved_changes: false } }]);
                    } else {
                        // Item updated or added
                        try {
                            const temp_obj = JSON.parse(event.newValue);
                            this.chatObjects.update([{ key: key, value: { temp_obj: temp_obj, unsaved_changes: true } }]);
                        } catch (e) {
                            console.error("Failed to parse temp_obj from storage event", e);
                        }
                    }
                });
            }
        }
    };

    constructor() {
        makeAutoObservable(this);
        this.loadFromDB();
        setInterval(this.flushToLocalDB, 1000);
        if (typeof window !== "undefined") {
            window.addEventListener("storage", this.storageListener);
        }
    }

    clear: () => void = () => {
        this.rememberCurrentSelection();
        this.flushToLocalDB();
        this.chatObjects = new SortedMap<ChatObject>();
        this.selectedObjectKey = null;
        this.conversation = [];
        this.conversationId = null;
        this.streamingContent = "";
        this.objects_page = 0;
        this.objects_maxpage = null;
        this._fetchGeneration++;
        this.tagIds = [];
        this.queuedSteers = [];
        this.state = "idle";

        this.loadFromDB();
    };

    setConversation = (val: ConversationBlock[], conversationId?: number, tagIds?: number[]) => {
        runInAction(() => {
            const prevId = this.conversationId;
            const switching = !!conversationId && conversationId !== prevId;
            if (switching && prevId != null) this.rememberCurrentSelection(prevId);

            this.conversation = val;
            if (conversationId) this.conversationId = conversationId;
            if (tagIds) this.tagIds = [...tagIds];
            if (!switching) return;

            const hasLiveSelection = this.selectedObjectKey != null || this.getSelected().length > 0;
            if (prevId == null && hasLiveSelection) {
                // A new conversation just got its id: it owns what the user already has open.
                this.rememberCurrentSelection(conversationId);
            } else {
                this.applyConversationSelection(conversationId!, val);
            }
        });
    };

    startStreaming = (agentId?: number, conversationId?: number, toolUse?: { name: string; id: string }) => {
        runInAction(() => {
            if (conversationId) this.conversationId = conversationId;

            let lastBlock = this.conversation[this.conversation.length - 1];
            // A tool_use carries no agent id of its own; it belongs to the assistant already talking.
            const blockAgentId = agentId ?? (lastBlock?.role === "assistant" ? lastBlock.agent_id : undefined);

            // The live placeholder renders the shared buffer, so the one we are leaving behind has to
            // keep its own content before the buffer is reused for the next block.
            const live = this.livePlaceholder();
            const liveBlock = Array.isArray(live?.content) ? live.content[0] : undefined;
            if (liveBlock?.type === "text") {
                if (this.streamingContent) {
                    liveBlock.text = this.streamingContent;
                } else {
                    // An empty text placeholder would keep reading the buffer, now holding the next block.
                    const owner = this.conversation.findIndex((block) => block.content.includes(live!));
                    this.conversation[owner].content = this.conversation[owner].content.filter((m) => m !== live);
                    if (this.conversation[owner].content.length === 0) this.conversation.splice(owner, 1);
                    lastBlock = this.conversation[this.conversation.length - 1];
                }
            } else if (liveBlock?.type === "tool_use") {
                liveBlock.partial_input = this.streamingContent;
            }

            this.streamingContent = "";

            const incompleteMsg: ChatMessage = {
                content: toolUse ? [{ type: "tool_use", id: toolUse.id, name: toolUse.name }] : [{ type: "text", text: "" }],
                role: "assistant",
                incomplete: true,
                agent_id: blockAgentId,
                conversation_id: conversationId,
            };

            if (lastBlock && lastBlock.role === "assistant" && lastBlock.agent_id === blockAgentId) {
                lastBlock.content.push(incompleteMsg);
            } else {
                this.conversation.push({ role: "assistant", agent_id: blockAgentId, content: [incompleteMsg] });
            }
        });
    };

    /** The placeholder the shared streaming buffer belongs to: the newest incomplete message. */
    livePlaceholder = (): ChatMessage | undefined => {
        for (let i = this.conversation.length - 1; i >= 0; i--) {
            const live = this.conversation[i].content.findLast((m) => m.incomplete);
            if (live) return live;
        }
        return undefined;
    };

    appendStreamingContent = (chunk: string) => {
        runInAction(() => {
            this.streamingContent += chunk;
        });
    };

    addTempUserMessage = (content: string) => {
        runInAction(() => {
            const tempMsg: ChatMessage = { content, role: "user", _temp: true };
            const lastBlock = this.conversation[this.conversation.length - 1];
            if (lastBlock && lastBlock.role === "user") {
                lastBlock.content.push(tempMsg);
            } else {
                this.conversation.push({ role: "user", content: [tempMsg] });
            }
        });
    };

    addQueuedSteer = (text: string) => {
        const id = this.nextQueuedSteerId++;
        runInAction(() => {
            this.queuedSteers.push({ id, text });
        });
        return id;
    };

    removeQueuedSteer = (id: number) => {
        runInAction(() => {
            this.queuedSteers = this.queuedSteers.filter((s) => s.id !== id);
        });
    };

    /** The run persisted a user message: drop the queued entry it came from, if any. */
    takeQueuedSteer = (text: string) => {
        runInAction(() => {
            if (this.queuedSteers.length === 0) return;
            const idx = this.queuedSteers.findIndex((s) => s.text.trim() === text.trim());
            this.queuedSteers.splice(idx === -1 ? 0 : idx, 1);
        });
    };

    clearQueuedSteers = () => {
        runInAction(() => {
            this.queuedSteers = [];
        });
    };

    /** Forget in-flight streaming state, e.g. before replaying a run snapshot. */
    dropIncomplete = () => {
        runInAction(() => {
            this.streamingContent = "";
            this.conversation = this.conversation
                .map((block) => ({ ...block, content: block.content.filter((m) => !m.incomplete) }))
                .filter((block) => block.content.length > 0);
        });
    };

    /** The run ended without a final message for the placeholder: keep what streamed. */
    finalizeIncomplete = () => {
        runInAction(() => {
            for (const block of this.conversation) {
                for (const m of block.content) {
                    if (!m.incomplete) continue;
                    const first = Array.isArray(m.content) ? m.content[0] : undefined;
                    if (first?.type === "text" && !first.text) first.text = this.streamingContent;
                    m.incomplete = undefined;
                }
            }
            this.streamingContent = "";
        });
    };

    private isToolResult = (message: ChatMessage) => {
        return message.content instanceof Array && message.content.length > 0 && message.content[0]["type"] === "tool_result";
    };

    private hasMessage = (id: number | undefined) => {
        if (id === undefined || id === null) return false;
        return this.conversation.some((block) => block.content.some((m) => m.id === id && !m.incomplete));
    };

    commitStreaming = (finalDetail: ChatMessage) => {
        runInAction(() => {
            this.streamingContent = "";
            const msg = { ...finalDetail, incomplete: undefined };
            const lastBlock = this.conversation[this.conversation.length - 1];

            // Replaying a run snapshot re-sends messages the history fetch already
            // loaded; the replayed placeholder for it goes, the message stays put.
            if (this.hasMessage(msg.id)) {
                if (lastBlock) {
                    if (msg.role === "assistant") lastBlock.content = lastBlock.content.filter((m) => !m.incomplete);
                    if (lastBlock.content.length === 0) this.conversation.pop();
                }
                return;
            }

            // One response commits one assistant message, which covers all of its placeholders
            // (e.g. a text placeholder followed by a tool_use placeholder). Replace the first in
            // place so its row is reused, and drop the rest.
            if (lastBlock && msg.role === "assistant") {
                const incompleteIdx = lastBlock.content.findIndex((m) => m.incomplete);
                if (incompleteIdx !== -1) {
                    lastBlock.content[incompleteIdx] = msg;
                    lastBlock.content = lastBlock.content.filter((m) => !m.incomplete);
                    return;
                }
            }

            // Check if this is a user message that should replace a temp message
            if (msg.role === "user") {
                for (let i = this.conversation.length - 1; i >= 0; i--) {
                    const block = this.conversation[i];
                    if (block.role !== "user") continue;
                    const tempIdx = block.content.findIndex((m) => m._temp);
                    if (tempIdx !== -1) {
                        block.content[tempIdx] = msg;
                        return;
                    }
                }
            }

            // No incomplete placeholder — this is a non-streamed complete message
            // (e.g. tool result). Apply the same block-switching logic as before.
            if (lastBlock) {
                const lastMessage = lastBlock.content[lastBlock.content.length - 1];

                // Duplicate check
                if (lastMessage && lastMessage.id === msg.id) {
                    return;
                }

                // Same role+agent, or tool result → append to existing block
                if ((lastBlock.role === msg.role && lastBlock.agent_id == msg.agent_id) || this.isToolResult(msg)) {
                    lastBlock.content.push(msg);
                    return;
                }
            }

            // Different role/agent → create a new conversation block
            this.conversation.push({
                role: msg.role,
                agent_id: msg.agent_id,
                content: [msg],
            });
        });
    };

    objects_page: number = 0;
    objects_maxpage: number | null = null;
    pageSize = 20;
    _fetchGeneration: number = 0;
}

// Only initialize appState on the client side
export const chatState = new ChatState();

const _fetchingRefs = new Set<string>();

// Maintains shared state between parent / child relations on sources + arts, where they always fetch eachother.
// A source is an event (clinical) or a document (research); both hold artifact_ids.
autorun(() => {
    // O(n) scan: iterate values once, O(1) lookups for each reference
    const linkArtifactToSource = (artifactId: number, sourceType: "event" | "document", sourceId: number) => {
        const k = (sourceType === "event" ? "E" : "D") + sourceId;
        const existingSource = chatState.chatObjects.get({ type: sourceType, id: sourceId });
        if (!existingSource && !_fetchingRefs.has(k)) {
            _fetchingRefs.add(k);
            chatState.getObjectByKey({ type: sourceType, id: sourceId }).finally(() => _fetchingRefs.delete(k));
            return;
        }
        if (!existingSource) return;
        const currentArtifactIds =
            sourceType === "event"
                ? ((existingSource as ChatEvent).event as calendar_event)?.artifact_ids
                : ((existingSource as ChatDocument).document as document_type)?.artifact_ids;
        if (currentArtifactIds && !currentArtifactIds.includes(artifactId)) {
            const nextIds = [...currentArtifactIds, artifactId];
            chatState.chatObjects.update([
                {
                    key: { type: sourceType, id: sourceId },
                    value:
                        sourceType === "event"
                            ? { event: { artifact_ids: nextIds } }
                            : { document: { artifact_ids: nextIds } },
                },
            ]);
        }
    };

    const fetchMissingArtifacts = (artifactIds: number[] | undefined) => {
        if (!artifactIds) return;
        for (const artId of artifactIds) {
            const k = "A" + artId;
            if (!chatState.chatObjects.get({ type: "artifact", id: artId })?.artifact && !_fetchingRefs.has(k)) {
                _fetchingRefs.add(k);
                chatState.getObjectByKey({ type: "artifact", id: artId }).finally(() => _fetchingRefs.delete(k));
            }
        }
    };

    for (const obj of chatState.chatObjects.values()) {
        if (obj.type === "artifact") {
            const artifact = (obj as ChatArtifact).artifact;
            if (artifact?.event_id) {
                linkArtifactToSource(obj.id, "event", artifact.event_id);
            } else if (artifact?.document_id) {
                linkArtifactToSource(obj.id, "document", artifact.document_id);
            }
        } else if (obj.type === "event") {
            fetchMissingArtifacts((obj.event as calendar_event)?.artifact_ids);
        } else if (obj.type === "document") {
            fetchMissingArtifacts((obj.document as document_type)?.artifact_ids);
        }
    }
});
